import { usePage } from "@inertiajs/react"
import { BrandMotif } from "@/components/ui/brand-motif"
import { useLocalePath, useSpanishFirstPath } from "@/lib/locale-path"
import { cn } from "@/lib/utils"
import type { PageProps } from "@/types/inertia"

// One admin-edited "door" (a therapy.doors feature-list item). The list's
// icon slot carries the page path the door opens onto — the registry labels
// it that way — so the admin can point a door anywhere on her site.
export type DoorItem = {
  icon?: string | null
  title_en?: string
  title_es?: string
  desc_en?: string
  desc_es?: string
}

// One published Therapy row: its short nav label, its page, and its blurb.
export type TherapyCard = { label: string; path: string; blurb: string | null }

interface Props {
  doors: DoorItem[]
  therapies?: TherapyCard[]
}

// "Where would you like to start?" — the switchboard under the founder band.
// Four doors (OCD, anxiety, trauma, Spanish by default) each opening onto the
// detail page for that problem, then "How I work": a row of smaller cards,
// one per therapy, answering the other question a visitor has — not what is
// wrong, but what we would actually do. The hero already lists her
// conditions, so this section never repeats them.
const BANDS = ["bg-teal", "bg-sage", "bg-teal-mid", "bg-stone-mid"]

export default function GiselaDoors({ doors, therapies = [] }: Props) {
  const { props } = usePage<PageProps>()
  const t = props.translations.gisela_pages.doors
  const lp = useLocalePath()
  const spanishFirst = useSpanishFirstPath()
  const locale = props.current_locale
  const onGiselaHost = props.current_brand.key === "gisela"
  const { brand_urls } = props
  const items = doors
    .map((d) => ({
      path: (d.icon ?? "").trim(),
      title: (locale === "es" && d.title_es) || d.title_en || "",
      desc: (locale === "es" && d.desc_es) || d.desc_en || "",
    }))
    .filter((d) => d.title !== "")

  if (items.length === 0) return null

  // Her paths only resolve on her own host; the venue's copy of this page
  // links across to her site instead.
  const href = (path: string) => {
    if (!path) return undefined
    // A door onto her Spanish landing page opens on the Spanish URL whatever
    // language the reader is in — the door is labelled in Spanish for the
    // same reason (see useSpanishFirstPath).
    const target = spanishFirst(path) ?? lp(path)
    return onGiselaHost ? target : `${brand_urls.gisela}${target}`
  }

  return (
    <section className="relative overflow-hidden bg-cream py-12 lg:py-14">
      <BrandMotif variant="disc" className="absolute top-5 -left-40" />
      <div className="relative mx-auto max-w-7xl px-6 lg:px-8">
        <div className="max-w-3xl">
          <h2>{t.heading}</h2>
          <p className="mt-3 text-ink-muted">{t.intro}</p>
        </div>

        <div className="mt-10 grid grid-cols-1 gap-5 sm:grid-cols-2 xl:grid-cols-4">
          {items.map((door, i) => (
            <a
              key={`${door.path}-${door.title}`}
              href={href(door.path)}
              className="group flex flex-col overflow-hidden rounded-2xl bg-white text-ink-body no-underline ring-1 ring-hairline transition-shadow hover:shadow-md"
            >
              <span aria-hidden className={cn("h-2.5 w-full", BANDS[i % BANDS.length])} />
              <span className="flex flex-grow flex-col gap-3 p-6 pb-7">
                <h3>{door.title}</h3>
                {door.desc && <span className="text-[15px] leading-relaxed text-ink-muted">{door.desc}</span>}
                <span className="mt-auto pt-2 text-sm font-medium text-teal underline underline-offset-4 group-hover:opacity-80">
                  {door.title} →
                </span>
              </span>
            </a>
          ))}
        </div>

        {therapies.length > 0 && (
          <div className="mt-14">
            <div className="max-w-3xl">
              <h2>{t.how_heading}</h2>
              <p className="mt-3 text-ink-muted">{t.how_intro}</p>
            </div>
            <div className="mt-8 grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
              {therapies.map((th, i) => (
                <a
                  key={th.path}
                  href={href(th.path)}
                  className="group flex flex-col overflow-hidden rounded-2xl bg-white text-ink-body no-underline ring-1 ring-hairline transition-shadow hover:shadow-md"
                >
                  <span aria-hidden className={cn("h-1.5 w-full", BANDS[(i + 1) % BANDS.length])} />
                  <span className="flex flex-grow flex-col gap-2 p-5 pb-6">
                    <h3>{th.label}</h3>
                    {th.blurb && <span className="text-sm leading-relaxed text-ink-muted">{th.blurb}</span>}
                    <span className="mt-auto pt-2 text-sm font-medium text-teal underline underline-offset-4 group-hover:opacity-80">
                      {t.how_link}
                    </span>
                  </span>
                </a>
              ))}
            </div>
            <a href={href("/therapies-i-offer")} className="mt-6 inline-block text-sm font-medium text-teal underline underline-offset-4">
              {t.not_sure}
            </a>
          </div>
        )}
      </div>
    </section>
  )
}
